import { CertsyDialog, useDialogHistoryManager } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import {
  TextLinkButton,
  Box,
  Text,
  TooltipRenderer,
  Inline,
  IconArrow,
} from 'braid-design-system';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';

import translations from './.vocab';

interface ComponentProps {
  linkAction: 'verify' | 'manage';
  linkUrl: string;
  hitArea?: 'standard' | 'large';
  onClick?: () => void;
}

export const RightToWorkCertsyLink = ({
  linkAction,
  hitArea = 'large',
  linkUrl,
  onClick,
}: ComponentProps) => {
  const { t } = useTranslations(translations);
  const [displayDialog, setDisplayDialog] = useState(false);
  const { openDialog, closeDialog } = useDialogHistoryManager(setDisplayDialog);

  const dispatch = useDispatch();

  const onClickFn = useCallback(() => {
    openDialog();
    onClick?.();
    dispatch(
      certsyLinkClicked({
        linkUrl,
        linkAction,
        linkPosition: 'main card',
        credentialType: 'RightToWork',
        currentPageSection: 'next role',
      }),
    );
  }, [dispatch, linkAction, linkUrl, onClick, openDialog]);

  const onClose = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  const certsyModalHeadingMap = {
    verify: t('Verify your Right to work'),
    manage: t('Manage your Right to work'),
  } as const;

  const certsyModalBodyTextMap = {
    verify: t('Help your application stand out...'),
    manage: t('Renew, remove or edit your Right to work on SEEK Pass'),
  } as const;

  const certsyLinkTextMap = {
    verify: t('Verify on SEEK Pass'),
    manage: t('Manage on SEEK Pass'),
  } as const;

  const certsyDialogLinkTextMap = {
    verify: t('Verify on SEEK Pass'),
    manage: t('Manage on SEEK Pass'),
  } as const;

  const certsyTooltipTextMap = {
    verify: t('Verify right to work'),
    manage: t('Manage right to work'),
  } as const;

  return (
    <>
      <Inline alignY="center" space="xsmall">
        <TooltipRenderer
          id="verify-right-to-work-tooltip"
          tooltip={<Text> {certsyTooltipTextMap[linkAction]}</Text>}
        >
          {({ triggerProps }) => (
            <Box
              aria-label={certsyTooltipTextMap[linkAction]}
              {...triggerProps}
            >
              <Text>
                <TextLinkButton
                  hitArea={hitArea}
                  onClick={onClickFn}
                  icon={
                    linkAction === 'verify' ? (
                      <IconArrow direction="right" />
                    ) : undefined
                  }
                  iconPosition="trailing"
                >
                  {certsyLinkTextMap[linkAction]}
                </TextLinkButton>
              </Text>
            </Box>
          )}
        </TooltipRenderer>
      </Inline>
      <CertsyDialog
        bodyText={certsyModalBodyTextMap[linkAction]}
        certsyButtonText={certsyDialogLinkTextMap[linkAction]}
        certsyUrl={linkUrl}
        title={certsyModalHeadingMap[linkAction]}
        open={displayDialog}
        onClose={onClose}
        width="small"
        onCertsyClick={() =>
          dispatch(
            certsyLinkClicked({
              linkUrl,
              linkAction,
              linkPosition: 'pop up',
              credentialType: 'RightToWork',
              currentPageSection: 'next role',
            }),
          )
        }
      />
    </>
  );
};
